// Comparisons behind the free calculators. Each runs the full estimate twice and reports the difference,
// so a payout's tax is measured on top of the user's other income rather than in isolation.
import { type Dec, ZERO, max0 } from "./money.ts";
import { estimateFederal, type EstimateInput, type EstimateResult } from "./estimate.ts";

type Computed = Extract<EstimateResult, { state: "computed" }>;
type NeedsInput = Extract<EstimateResult, { state: "needs_input" }>;
type Invalid = Extract<EstimateResult, { state: "invalid" }>;
type Blocked = NeedsInput | Invalid;

/** Everything about the user except the item being measured. */
export type BaseInput = Omit<EstimateInput, "propNetProfit" | "net1256"> & {
  /** Other prop profit already earned this year (net of expenses). */
  otherPropNetProfit: Dec;
  /** Net Section 1256 gain or (loss) from the user's own accounts. */
  net1256: Dec;
};

function run(base: BaseInput, extraProp: Dec, extra1256: Dec, extraOrdinary: Dec): EstimateResult {
  return estimateFederal({
    status: base.status,
    wages: base.wages,
    traderSocialSecurityWages: base.traderSocialSecurityWages,
    medicareWages: base.medicareWages,
    withholding: base.withholding,
    interest: base.interest,
    otherOrdinaryIncome: base.otherOrdinaryIncome.plus(extraOrdinary),
    qbi: base.qbi,
    propNetProfit: base.otherPropNetProfit.plus(extraProp),
    net1256: base.net1256.plus(extra1256),
  });
}

function firstBlocked(...rs: EstimateResult[]): Blocked | null {
  return (rs.find((r) => r.state === "invalid") as Invalid | undefined) ?? (rs.find((r) => r.state === "needs_input") as NeedsInput | undefined) ?? null;
}

export type PropPayoutResult =
  | Blocked
  | {
      state: "computed";
      netProfit: Dec;
      /** Extra federal tax caused by these payouts. */
      taxOnPayouts: Dec;
      seTaxPart: Dec;
      incomeTaxPart: Dec;
      /** Share of net profit to set aside. Null when there is no profit. */
      setAsideRate: Dec | null;
      takeHome: Dec;
      withPayouts: Computed;
      withoutPayouts: Computed;
    };

/** Tax on a year of prop payouts, measured on top of the user's other income. */
export function propPayoutTax(base: BaseInput, grossPayouts: Dec, expenses: Dec): PropPayoutResult {
  const bad = [grossPayouts.lessThan(0) && "grossPayouts", expenses.lessThan(0) && "expenses"].filter((x): x is string => Boolean(x));
  if (bad.length) return { state: "invalid", errors: bad, trace: [] };
  const netProfit = grossPayouts.minus(expenses);
  const withP = run(base, netProfit, ZERO, ZERO);
  const without = run(base, ZERO, ZERO, ZERO);
  const ni = firstBlocked(withP, without);
  if (ni) return ni;
  const w = withP as Computed;
  const wo = without as Computed;
  const taxOnPayouts = w.totalTax.minus(wo.totalTax);
  const seTaxPart = w.seTax.minus(wo.seTax).plus(w.additionalMedicareTax.minus(wo.additionalMedicareTax));
  return {
    state: "computed",
    netProfit,
    taxOnPayouts,
    seTaxPart,
    incomeTaxPart: taxOnPayouts.minus(seTaxPart),
    setAsideRate: netProfit.greaterThan(0) ? max0(taxOnPayouts).div(netProfit) : null,
    takeHome: netProfit.minus(taxOnPayouts),
    withPayouts: w,
    withoutPayouts: wo,
  };
}

export type ComparisonResult =
  | Blocked
  | {
      state: "computed";
      /** Extra tax under each treatment, on top of the user's other income. */
      taxA: Dec;
      taxB: Dec;
      /** taxB − taxA: positive means treatment A costs less. */
      difference: Dec;
      a: Computed;
      b: Computed;
      baseline: Computed;
    };

function compare(base: BaseInput, a: EstimateResult, b: EstimateResult): ComparisonResult {
  const baseline = run(base, ZERO, ZERO, ZERO);
  const ni = firstBlocked(a, b, baseline);
  if (ni) return ni;
  const ca = a as Computed;
  const cb = b as Computed;
  const c0 = baseline as Computed;
  const taxA = ca.totalTax.minus(c0.totalTax);
  const taxB = cb.totalTax.minus(c0.totalTax);
  return { state: "computed", taxA, taxB, difference: taxB.minus(taxA), a: ca, b: cb, baseline: c0 };
}

/** A futures gain taxed under 60/40 (A) versus the same amount taxed as ordinary income (B). */
export function sixtyFortyVsOrdinary(base: BaseInput, gain: Dec): ComparisonResult {
  return compare(base, run(base, ZERO, gain, ZERO), run(base, ZERO, ZERO, gain));
}

/** The same profit earned in a personal futures account (A, 60/40) versus as prop payouts (B, Schedule C + SE). */
export function personalVsProp(base: BaseInput, profit: Dec): ComparisonResult {
  return compare(base, run(base, ZERO, profit, ZERO), run(base, profit, ZERO, ZERO));
}
